import * as XLSX from 'xlsx';
import type { PlanData } from '../types/planDownload';

/**
 * Exports plan data to Excel format and triggers download
 * @param data - Array of PlanData to export
 * @param dateFrom - Start date of the filter range
 * @param dateTo - End date of the filter range
 * @param activePremiumOnly - Whether only active premium data is included
 */
export const exportPlanDataToExcel = (
  data: PlanData[],
  dateFrom: string,
  dateTo: string,
  activePremiumOnly: boolean
): void => {
  try {
    // Create a new workbook
    const workbook = XLSX.utils.book_new();

    // Define column headers matching the preview table
    const headers = [
      'PRODUCT CODE',
      'PLAN CODE',
      'HIS PLAN ID',
      'HIS REV NO',
      'PLAN DESCRIPTION',
      'EFFECTIVE DATE',
      'EXPIRATION DATE',
      'MIN DAYS',
      'MAX DAYS',
      'CURRENCY',
      'GROSS PREMIUM',
      'NET PREMIUM',
      'MRL',
      'COVERAGE CODE',
      'AVAILABLE FOR SALES AGENT',
      'OCC TEMPLATE',
      'IS CANCELLED',
      'OCC PREFIX',
      'TACTIC TEMPLATE',
      'APP FORM TEMPLATE',
      'PLAN TAX',
      'LOCAL TAX',
      'DST'
    ];

    // Transform data to match Excel format
    const excelData = data.map(item => [
      item.productCode,
      item.planCode,
      item.hisPlanId,
      item.hisRevNo,
      item.planDescription,
      item.effectiveDate,
      item.expirationDate,
      item.minimumDays,
      item.maximumDays,
      item.currency,
      item.grossPremium,
      item.netPremium,
      item.mrl,
      item.coverageCode,
      item.availableForSalesAgent ? 'TRUE' : 'FALSE',
      item.occTemplate,
      item.isCancelled ? 'TRUE' : 'FALSE',
      item.occPrefix,
      item.tacticTemplate,
      item.appFormTemplate,
      item.planTax,
      item.localTax,
      item.gst
    ]);

    // Combine headers with data
    const worksheetData = [headers, ...excelData];

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths for better readability
    const columnWidths = [
      { wch: 12 }, // PRODUCT CODE
      { wch: 10 }, // PLAN CODE
      { wch: 12 }, // HIS PLAN ID
      { wch: 10 }, // HIS REV NO
      { wch: 30 }, // PLAN DESCRIPTION
      { wch: 15 }, // EFFECTIVE DATE
      { wch: 15 }, // EXPIRATION DATE
      { wch: 10 }, // MIN DAYS
      { wch: 10 }, // MAX DAYS
      { wch: 10 }, // CURRENCY
      { wch: 15 }, // GROSS PREMIUM
      { wch: 15 }, // NET PREMIUM
      { wch: 12 }, // MRL
      { wch: 15 }, // COVERAGE CODE
      { wch: 25 }, // AVAILABLE FOR SALES AGENT
      { wch: 40 }, // OCC TEMPLATE
      { wch: 12 }, // IS CANCELLED
      { wch: 12 }, // OCC PREFIX
      { wch: 40 }, // TACTIC TEMPLATE
      { wch: 40 }, // APP FORM TEMPLATE
      { wch: 12 }, // PLAN TAX
      { wch: 12 }, // LOCAL TAX
      { wch: 10 }  // DST
    ];

    worksheet['!cols'] = columnWidths;

    // Apply header formatting
    const headerRange = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!worksheet[cellAddress]) continue;
      
      worksheet[cellAddress].s = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '2563EB' } },
        alignment: { horizontal: 'center', vertical: 'center' }
      };
    }

    // Format numeric columns
    for (let row = 1; row <= data.length; row++) {
      // Format GROSS PREMIUM (column K, index 10)
      const grossPremiumCell = XLSX.utils.encode_cell({ r: row, c: 10 });
      if (worksheet[grossPremiumCell]) {
        worksheet[grossPremiumCell].z = '#,##0.00';
      }

      // Format NET PREMIUM (column L, index 11)
      const netPremiumCell = XLSX.utils.encode_cell({ r: row, c: 11 });
      if (worksheet[netPremiumCell]) {
        worksheet[netPremiumCell].z = '#,##0.00';
      }

      // Format PLAN TAX (column U, index 20)
      const planTaxCell = XLSX.utils.encode_cell({ r: row, c: 20 });
      if (worksheet[planTaxCell]) {
        worksheet[planTaxCell].z = '#,##0.00';
      }

      // Format LOCAL TAX (column V, index 21)
      const localTaxCell = XLSX.utils.encode_cell({ r: row, c: 21 });
      if (worksheet[localTaxCell]) {
        worksheet[localTaxCell].z = '#,##0.00';
      }

      // Format DST (column W, index 22)
      const dstCell = XLSX.utils.encode_cell({ r: row, c: 22 });
      if (worksheet[dstCell]) {
        worksheet[dstCell].z = '#,##0.00';
      }
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Plan Data');

    // Create metadata sheet with export information
    const metadataHeaders = ['Export Information', 'Value'];
    const metadataData = [
      metadataHeaders,
      ['Export Date', new Date().toLocaleString()],
      ['Date Range From', dateFrom],
      ['Date Range To', dateTo],
      ['Active Premium Only', activePremiumOnly ? 'Yes' : 'No'],
      ['Total Records', data.length.toString()],
      ['Generated By', 'MyTravelSafe Plan Download']
    ];

    const metadataWorksheet = XLSX.utils.aoa_to_sheet(metadataData);
    metadataWorksheet['!cols'] = [{ wch: 20 }, { wch: 30 }];

    // Format metadata headers
    metadataWorksheet['A1'].s = {
      font: { bold: true },
      fill: { fgColor: { rgb: 'E5E7EB' } }
    };
    metadataWorksheet['B1'].s = {
      font: { bold: true },
      fill: { fgColor: { rgb: 'E5E7EB' } }
    };

    XLSX.utils.book_append_sheet(workbook, metadataWorksheet, 'Export Info');

    // Generate filename with timestamp
    const timestamp = new Date().toISOString()
      .replace(/[-:]/g, '')
      .replace(/\..+/, '')
      .replace('T', '_');
    
    const formattedDateFrom = dateFrom.replace(/\//g, '-');
    const formattedDateTo = dateTo.replace(/\//g, '-');
    
    const filename = `Plan_Download_${formattedDateFrom}_to_${formattedDateTo}_${timestamp}.xlsx`;

    // Write and download the file
    XLSX.writeFile(workbook, filename);

  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw new Error('Failed to export data to Excel. Please try again.');
  }
};